import { LitElement, css, html } from "lit";
import { keyed } from "lit/directives/keyed.js";
import { defineChromeElement } from "../../chrome/ChromeElement";
import { chromeControlStyles } from "../../chrome/chromeStyles";
import { fitTexts } from "../../chrome/fitText";
import type { BetChangePopupPort } from "./BetChangePopupPort";

/** Visible 1 s, then 0.18 s fading out and shrinking to 0.86. */
const TIMING = { visibleSeconds: 1, exitSeconds: 0.18, hiddenScale: 0.86 } as const;

export const BET_CHANGE_POPUP_TAG = "slot-bet-change-popup";

/**
 * `<slot-bet-change-popup>` (mobile): the new bet in a white box for a second, then the fade-out;
 * the cycle is one CSS animation whose end hides the popup and notifies the presenter.
 */
export class BetChangePopupElement extends LitElement implements BetChangePopupPort {
  public static override properties = {
    label: { type: String },
    shown: { type: Boolean, reflect: true },
    value: { state: true },
    cycle: { state: true },
  };

  public declare label: string;
  public declare shown: boolean;
  private declare value: string;
  /** Bumped per `show` so the animation restarts (a keyed node). */
  private declare cycle: number;
  private onHidden: (() => void) | undefined;

  public constructor() {
    super();
    this.label = "Bet";
    this.shown = false;
    this.value = "";
    this.cycle = 0;
  }

  public static override styles = [
    chromeControlStyles,
    css`
      :host { display: block; position: relative; width: 361px; height: 105.3px; pointer-events: none; }
      :host(:not([shown])) { display: none; }
      .box { position: absolute; inset: 0; border-radius: 8px; background: rgba(255, 255, 255, ${Number((198 / 255).toFixed(3))}); color: #000; font-size: 34.2px; font-weight: 500; animation: bet-popup ${TIMING.visibleSeconds + TIMING.exitSeconds}s linear forwards; transform-origin: center; }
      @keyframes bet-popup {
        0%, ${Number(((TIMING.visibleSeconds / (TIMING.visibleSeconds + TIMING.exitSeconds)) * 100).toFixed(2))}% { opacity: 1; transform: scale(1); }
        100% { opacity: 0; transform: scale(${TIMING.hiddenScale}); }
      }
      .row { position: absolute; left: 50%; transform: translate(-50%, -50%); width: 164.7px; height: 50.4px; display: flex; align-items: center; justify-content: center; }
    `,
  ];

  public show(valueText: string): void {
    this.value = valueText;
    this.shown = true;
    this.cycle += 1;
  }

  public hideImmediate(notify = true): void {
    this.shown = false;
    if (notify) this.onHidden?.();
  }

  public setOnHidden(callback: (() => void) | undefined): void {
    this.onHidden = callback;
  }

  public isVisible(): boolean {
    return this.shown;
  }

  private onAnimationEnd = (): void => {
    if (this.shown) this.hideImmediate();
  };

  protected override updated(): void {
    fitTexts(this.renderRoot);
  }

  protected override render() {
    // `keyed` is what makes the restart real: lit discards the previous box and builds a new one
    // whenever the cycle changes, and a fresh element restarts the CSS animation. Binding the cycle
    // as an attribute updates the SAME node, and a running CSS animation does not restart when an
    // attribute changes — so a burst of bet taps measured its 1 s dwell from the FIRST tap and the
    // popup vanished early. The Cocos original does the equivalent: `BetChangePopupPanel.show`
    // stops the running tween and resets scale and opacity before starting the delay again.
    return keyed(
      this.cycle,
      html`<div class="box" data-cycle=${this.cycle} @animationend=${this.onAnimationEnd}>
        <div class="row" style="top: ${52.65 - 23.97}px"><span data-fit>${this.label}</span></div>
        <div class="row" data-part="value" style="top: ${52.65 + 21.29}px"><span data-fit>${this.value}</span></div>
      </div>`,
    );
  }
}

export function defineBetChangePopupElement(): void {
  defineChromeElement(BET_CHANGE_POPUP_TAG, BetChangePopupElement);
}
